/**
 * The persona layer: the answers it reads, and the copy each answer selects.
 *
 * Everything here is a pure lookup. Resolve assigns a random persona when the input is
 * missing; we don't — a random persona makes an emotional claim about someone we know
 * nothing about, and it's what makes "the personalization is an illusion" true.
 *
 * Tone guardrail, same as `copy.ts`: every string is about the bill. Nothing references
 * a pregnancy, a baby, or a test result, and nothing congratulates.
 */

/** 1–5, as shown on the stress scale. */
export type StressLevel = 1 | 2 | 3 | 4 | 5;

export type Feeling = "confused" | "blindsided" | "worried" | "ready";

/**
 * Internal identifiers. None of these is ever shown to a member, and none should be —
 * nobody wants to be told they're "The Squeezed".
 */
export type PersonaId =
  | "decoder"
  | "overwhelmed"
  | "skeptic"
  | "blindsided"
  | "planner"
  | "squeezed"
  | "delegator";

export const STRESS_LEVELS: readonly StressLevel[] = [1, 2, 3, 4, 5];

/**
 * Three answers recorded on the 1-5 scale everything downstream still reads.
 * 5/3/1 keeps "A lot" inside persona's strained band and leaves the middle
 * option clear of it.
 */
export const STRESS_OPTIONS: readonly {
  id: string;
  label: string;
  level: StressLevel;
}[] = [
  { id: "a-lot", label: "A lot", level: 5 },
  { id: "a-little", label: "A little", level: 3 },
  { id: "not-really", label: "Not really", level: 1 },
];

/**
 * Where the scale splits. 3/4 rather than 2/3 because a 1–5 scale, asked of someone who
 * clicked an ad about their bill, piles up at 3–4 — splitting at 2/3 sends nearly
 * everyone to the strained copy and quietly turns seven personas back into four. Worth
 * revisiting against real distribution.
 */
const STRAINED_FROM = 4;

/**
 * Readiness doesn't split by band: it reads the same whatever the stress answer, and
 * splitting it produced two personas with no distinct copy.
 */
const PERSONA_BY_FEELING: Record<
  Feeling,
  { steady: PersonaId; strained: PersonaId }
> = {
  confused: { steady: "decoder", strained: "overwhelmed" },
  blindsided: { steady: "skeptic", strained: "blindsided" },
  worried: { steady: "planner", strained: "squeezed" },
  ready: { steady: "delegator", strained: "delegator" },
};

export const personaFor = (
  stressLevel: StressLevel,
  feeling: Feeling,
): PersonaId =>
  PERSONA_BY_FEELING[feeling][
    stressLevel >= STRAINED_FROM ? "strained" : "steady"
  ];

export const FEELINGS: readonly { id: Feeling; label: string }[] = [
  { id: "confused", label: "I don't understand the charges" },
  { id: "blindsided", label: "It's unexpected" },
  { id: "worried", label: "I can't afford it" },
  { id: "ready", label: "I just want it handled" },
];

/**
 * The emotional moment: the one screen that asks for nothing. Keyed off the concern
 * rather than the persona — the stress band changes the funnel's tone elsewhere, not
 * what this screen says back. Empathy about the bill situation; never about the test,
 * the result, or the reason for either.
 */
export const personaValidationCopy: Record<
  Feeling,
  { title: string; body: string }
> = {
  confused: {
    title: "We'll get to the bottom of this",
    body: "Natera statements are genuinely hard to read. We'll break down the charges and work to lower your bill.",
  },
  blindsided: {
    title: "We'll get to the bottom of this",
    body: "Natera bills tend to show up unannounced. We'll find out where this one came from — and whether you should owe it at all.",
  },
  worried: {
    title: "We can help bring it down",
    body: "The statement price is a starting point. Between Natera's discount program and negotiating, most bills end up far lower.",
  },
  ready: {
    title: "Let us take care of this for you",
    body: "A few more questions and we take it from here — no calls, no chasing, no hold music.",
  },
};

/**
 * The same screen for members whose bill isn't Natera's. They answer the same three
 * questions and get the same acknowledgement, said without naming a lab we can't
 * help them with — it's the last thing they see before regular sign-up, so it has
 * to stand on its own rather than trail off into a funnel they aren't entering.
 */
export const acknowledgeStress = (
  title: string,
  stressLevel: StressLevel,
): string => (stressLevel >= STRAINED_FROM ? `We hear you. ${title}` : title);

export const generalValidationCopy: Record<
  Feeling,
  { title: string; body: string }
> = {
  confused: {
    title: "We'll get to the bottom of this",
    body: "Medical bills are genuinely hard to read. We'll break down the charges and work to lower what you owe.",
  },
  blindsided: {
    title: "We'll get to the bottom of this",
    body: "Bills like this tend to show up unannounced. We'll find out where this one came from — and whether you should owe it at all.",
  },
  worried: {
    title: "We can help bring it down",
    body: "The statement price is a starting point. Between income-based discounts and negotiating, most bills end up far lower.",
  },
  ready: {
    title: "Let us take care of this for you",
    body: "We'll take it from here — no calls, no chasing, no hold music.",
  },
};
